import React, { useEffect, useRef, useState } from 'react'
import useAvailabilityCheck from '../../helper/useAvailabilityCheck';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import toast from 'react-hot-toast';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import ContentLoader from '../loader/ContentLoader';
import { suppliersService } from '../../services/suppliers.service';
import { pincodeService } from '../../services/pincode.service';
import { countriesService } from '../../services/countries.service';
import { statesService } from '../../services/states.service';
import { citiesService } from '../../services/cities.service';
import { SOMETHING_WENT_WRONG } from '../../constants/strings';
import PasswordInput from '../common/PasswordInput';
import { BANK_TYPES } from '../../types/signup.types';
import { BANKS } from '../../constants/banks';
import { formAvailabilityMsg, formField, formInputStyle, formLabelStyle, formReadOnlyStyle, formSectionCard, formSectionTitle, formSelectStyles } from "../common/FormUI";

const GRAD = "linear-gradient(135deg, var(--btn-bg) 0%, var(--btn-hover-bg) 100%)";

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];
// Account status also supports "pending" (self-registered suppliers awaiting approval)
const ACCOUNT_STATUS_OPTIONS = [{ value: "pending", label: "Pending" }, ...STATUS_OPTIONS];

const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const ACCOUNT_NUMBER_REGEX = /^\d{9,18}$/;
const BANK_FIELDS = ["account_holder_name", "account_number", "bank_name", "branch_name", "account_type", "ifsc_code"];
const ACCOUNT_TYPE_OPTIONS = BANK_TYPES.map(t => ({ value: t, label: t.charAt(0).toUpperCase() + t.slice(1) }));

const initialSupplier = {
  account_holder_name: "",
  account_number: "",
  bank_name: "",
  branch_name: "",
  account_type: "",
  ifsc_code: "",
  supplier_code: "",
  custom_code: "",
  agency_name: "",
  title: "Mr.",
  first_name: "",
  last_name: "",
  gender: "",
  website_url: "",
  mobile: "",
  whatsapp_number: "",
  email: "",
  address: "",
  pincode: "",
  country_id: "",
  state_id: "",
  city_id: "",
  status: "active",
  inventory_status: "active",
  password: "",
};

const AddSupplier = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const editSupplierCode = location.state?.supplierCode;
  const isEdit = !!editSupplierCode;

  const [loading, setLoading] = useState(false);
  // numeric id of the supplier being edited; the update API is keyed by id, the detail API by code
  const [editSupplierId, setEditSupplierId] = useState(null);
  const availability = useAvailabilityCheck(suppliersService);
  // values of the record being edited; they are not "taken" by themselves
  const originalRef = useRef({});
  const [supplier, setSupplier] = useState(initialSupplier);
  // option values are ids (the supplier API stores country_id/state_id/city_id)
  const [countryOptions, setCountryOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);

  useEffect(() => {
    (async () => {
      const countries = await loadCountryOptions();
      if (isEdit) {
        loadSupplierDetail();
      } else {
        const india = countries.find(c => c.label === "India");
        if (india) {
          setSupplier(v => ({ ...v, country_id: india.value }));
          loadStateOptions(india.value);
        }
      }
    })();
  }, [editSupplierCode]);

  const toOptions = (list) => (list || []).map(i => ({ value: i.id, label: i.name }));

  const loadCountryOptions = async () => {
    try {
      const res = await countriesService.list({ page: 1, status: "active" });
      const opts = toOptions(res?.resData?.data);
      setCountryOptions(opts);
      return opts;
    } catch (err) {
      setCountryOptions([]);
      return [];
    }
  }

  const loadStateOptions = async (countryId) => {
    if (!countryId) { setStateOptions([]); return []; }
    try {
      const res = await statesService.list({ page: 1, status: "active", country_id: countryId });
      const opts = toOptions(res?.resData?.data);
      setStateOptions(opts);
      return opts;
    } catch (err) {
      setStateOptions([]);
      return [];
    }
  }

  const loadCityOptions = async (stateId) => {
    if (!stateId) { setCityOptions([]); return []; }
    try {
      const res = await citiesService.list({ page: 1, status: "active", state_id: stateId });
      const opts = toOptions(res?.resData?.data);
      setCityOptions(opts);
      return opts;
    } catch (err) {
      setCityOptions([]);
      return [];
    }
  }

  // reload the dependent dropdowns for a country/state given by id
  const syncLocationOptions = async (countryId, stateId) => {
    await loadStateOptions(countryId);
    await loadCityOptions(stateId);
  }

  const showSwal = (title, text, icon) => {
    withReactContent(Swal).fire({ title, text, icon })
  }

  const loadSupplierDetail = async () => {
    try {
      setLoading(true);
      const res = await suppliersService.detailsByCode(editSupplierCode);
      if (res?.success) {        
        let v = res.resData;
        // by-code response may omit contact fields; fill the gaps from the by-id endpoint
        if (v?.id && (!v.mobile || !v.email)) {
          const full = await suppliersService.details(v.id);
          const f = full?.resData?.supplier ?? full?.resData;
          if (full?.success && f) v = { ...f, ...Object.fromEntries(Object.entries(v).filter(([, val]) => val !== null && val !== undefined && val !== "")) };
        }
        setEditSupplierId(v?.id ?? null);
        originalRef.current = { mobile: v?.mobile || "", email: (v?.email || "").toLowerCase(), custom_code: (v?.custom_code || "").toUpperCase() };
        const bank = (v?.bank_details || []).find(b => b?.is_primary) || v?.bank_details?.[0];
        setSupplier({
          account_holder_name: bank?.account_holder_name || "",
          account_number: bank?.account_number || "",
          bank_name: bank?.bank_name || "",
          branch_name: bank?.branch_name || "",
          account_type: bank?.account_type || "",
          ifsc_code: bank?.ifsc_code || "",
          supplier_code: v?.supplier_code || "",
          custom_code: v?.custom_code || "",
          agency_name: v?.agency_name || "",
          title: v?.title || "Mr.",
          first_name: v?.first_name || "",
          last_name: v?.last_name || "",
          gender: v?.gender || "",
          website_url: v?.website_url || "",
          mobile: v?.mobile || "",
          whatsapp_number: v?.whatsapp_number || "",
          email: v?.email || "",
          address: v?.address || "",
          pincode: v?.pincode || "",
          country_id: v?.country_id ?? "",
          state_id: v?.state_id ?? "",
          city_id: v?.city_id ?? "",
          status: v?.status || "active",
          inventory_status: v?.inventory_status || "active",
          password: "",
        });
        await syncLocationOptions(v?.country_id, v?.state_id);
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
    } finally {
      setLoading(false);
    }
  }

  const handleChange = (e) => {
    const { name } = e.target;
    let { value } = e.target;
    if (["custom_code", "account_holder_name", "branch_name"].includes(name)) value = value.toUpperCase();
    if (name === "email") availability.check("email", "email", value.trim().toLowerCase(), { message: "Email already exists", valid: /^\S+@\S+\.\S+$/.test(value.trim()), skip: value.trim().toLowerCase() === originalRef.current.email });
    if (name === "custom_code") availability.check("custom_code", "custom_code", value.trim(), { message: "Supplier code already exists", skip: value.trim() === originalRef.current.custom_code });
    if (name === "ifsc_code") value = value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 11);
    if (name === "account_number") value = value.replace(/\D/g, "").slice(0, 18);
    // Mr. -> male, Mrs./Ms. -> female; Dr. leaves gender as chosen
    if (name === "title") return setSupplier({ ...supplier, title: value, gender: { "Mr.": "male", "Mrs.": "female", "Ms.": "female" }[value] || supplier.gender });
    setSupplier({ ...supplier, [name]: value });
  }

  const handleMobileChange = (e) => {
    const mobile = e.target.value.replace(/\D/g, "").slice(0, 10);
    availability.check("mobile", "phone", mobile, { message: "Mobile already exists", valid: /^\d{10}$/.test(mobile), skip: mobile === originalRef.current.mobile });
    // WhatsApp mirrors mobile until it is edited separately
    const syncWhatsapp = !supplier.whatsapp_number || supplier.whatsapp_number === supplier.mobile;
    setSupplier({ ...supplier, mobile, whatsapp_number: syncWhatsapp ? mobile : supplier.whatsapp_number });
  }

  const handlePincodeChange = async (e) => {
    const pin = e.target.value.replace(/\D/g, "").slice(0, 6);
    setSupplier((v) => ({ ...v, pincode: pin }));
    if (pin.length !== 6) return;
    try {
      const res = await pincodeService.lookup(pin);
      if (res?.success) {
        // the pincode lookup returns names; map them to ids via the option lists
        const byName = (opts, name) => opts.find(o => o.label === name);
        const country = byName(countryOptions, res.resData?.countries?.[0]);
        const states = country ? await loadStateOptions(country.value) : [];
        const state = byName(states, res.resData?.states?.[0]);
        const cities = state ? await loadCityOptions(state.value) : [];
        const city = byName(cities, res.resData?.cities?.[0]);
        setSupplier((v) => ({
          ...v,
          pincode: pin,
          country_id: country?.value ?? v.country_id,
          state_id: country ? (state?.value ?? "") : v.state_id,
          city_id: state ? (city?.value ?? "") : v.city_id,
        }));
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
    }
  }

  const handleCountryChange = (o) => {
    setSupplier({ ...supplier, country_id: o ? o.value : "", state_id: "", city_id: "" });
    setCityOptions([]);
    loadStateOptions(o?.value);
  }

  const handleStateChange = (o) => {
    setSupplier({ ...supplier, state_id: o ? o.value : "", city_id: "" });
    loadCityOptions(o?.value);
  }

  const validate = () => {    
    if (!supplier.first_name.trim()) return "First name is required";
    if (!supplier.last_name.trim()) return "Last name is required";
    if (!supplier.gender) return "Gender is required";
    if (!/^\d{10}$/.test(supplier.mobile)) return "Enter a valid 10 digit mobile number";
    if (supplier.whatsapp_number && !/^\d{10}$/.test(supplier.whatsapp_number)) return "Enter a valid 10 digit WhatsApp number";
    if (!/^\S+@\S+\.\S+$/.test(supplier.email)) return "Enter a valid email address";
    if (!supplier.custom_code.trim()) return "Supplier code is required";
    if (availability.taken.mobile || availability.taken.email || availability.taken.custom_code) return availability.taken.mobile || availability.taken.email || availability.taken.custom_code;
    if (availability.isChecking) return "Please wait while availability is being checked";
    if (!supplier.agency_name.trim()) return "Agency name is required";
    if (!supplier.address.trim()) return "Address is required";    
    if (!supplier.city_id || !supplier.state_id || !supplier.country_id) return "Country, state and city are required";
    if (!/^\d{6}$/.test(supplier.pincode)) return "Enter a valid 6 digit pincode";        
    // bank details are mandatory on create; on update they may stay empty
    if (!isEdit || BANK_FIELDS.some(k => supplier[k])) {
      if (!supplier.account_holder_name.trim()) return "Account holder name is required";
      if (!ACCOUNT_NUMBER_REGEX.test(supplier.account_number)) return "Enter a valid account number (9-18 digits)";
      if (!supplier.bank_name) return "Bank name is required";
      if (!supplier.branch_name.trim()) return "Branch name is required";
      if (!supplier.account_type) return "Account type is required";
      if (!IFSC_REGEX.test(supplier.ifsc_code)) return "Enter a valid IFSC code (e.g. SBIN0001234)";
    }
    if (supplier.password && supplier.password.length < 6) return "Password must be at least 6 characters";    
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    const confirm = await withReactContent(Swal).fire({
      title: isEdit ? "Update supplier?" : "Save supplier?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: isEdit ? "Yes, update" : "Yes, save",
      cancelButtonText: "Cancel"
    });
    if (!confirm.isConfirmed) return;

    try {
      setLoading(true);
      const { supplier_code, password, country_id, state_id, city_id, account_holder_name, account_number, bank_name, branch_name, account_type, ifsc_code, ...others } = supplier;
      const rest = { ...others, country_id: String(country_id), state_id: String(state_id), city_id: String(city_id) };
      if (BANK_FIELDS.some(k => supplier[k])) {
        rest.bank_details = [{
          account_holder_name: account_holder_name.trim(), account_number, bank_name,
          branch_name: branch_name.trim(), account_type, ifsc_code, is_primary: true,
        }];
      }
      // password is sent only when filled
      const passwordPayload = password ? { ...rest, password } : rest;
      const res = isEdit
        ? await suppliersService.update(editSupplierId, passwordPayload)
        : await suppliersService.create({ ...passwordPayload, supplier_docs: [] });
      if (res?.success) {
        await withReactContent(Swal).fire({ title: "Success", text: res.message, icon: "success" });
        if (isEdit) {
          navigate("/suppliers/list");
        } else {
          const india = countryOptions.find(c => c.label === "India");
          setSupplier({ ...initialSupplier, country_id: india?.value ?? "" });
          setCityOptions([]);
          if (india) loadStateOptions(india.value); else setStateOptions([]);
        }
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
    } finally {
      setLoading(false);
    }
  }

  if (loading) return (<ContentLoader loaderType="circleSpin" />)

  const labelStyle = formLabelStyle;
  const inputStyle = formInputStyle;
  const readOnlyStyle = formReadOnlyStyle;
  const selectStyles = formSelectStyles;
  const sectionCard = formSectionCard;
  const sectionTitle = formSectionTitle;
  const field = (label, required, node, cls = "col-12 col-md-6 col-lg-4") => formField(label, required, node, cls);
  // keep a saved bank selectable even if it was later hidden from the list
  const bankOptions = [
    ...BANKS.filter(b => b.isShow).map(b => ({ value: b.name, label: b.name })),
    ...(supplier.bank_name && !BANKS.some(b => b.isShow && b.name === supplier.bank_name)
      ? [{ value: supplier.bank_name, label: supplier.bank_name }] : []),
  ];
  const title = isEdit ? "Update Supplier" : "Add Supplier";
  const pageTitle = isEdit ? "Update Supplier" : "Add New Supplier";

  return (
    <>
      <section className="section dashboard">
        <div className="d-flex align-items-start justify-content-between flex-wrap gap-2 mb-3">
          <div>
            <h4 className="m-0 fw-bold" style={{ color: "var(--page-heading)", fontSize: 20 }}>{pageTitle}</h4>
            <nav className="d-flex align-items-center gap-1 mt-1" style={{ fontSize: 12, color: "#475569" }}>
              <Link to="/dashboard" style={{ color: "#475569" }}><i className="bi bi-house-door"></i></Link>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <Link to="/suppliers/list" style={{ color: "#475569" }}>Suppliers</Link>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <span className="fw-semibold" style={{ color: "#4a5568" }}>{title}</span>
            </nav>
          </div>
          <Link to="/suppliers/list" className="btn btn-sm d-flex align-items-center gap-1 text-white px-3"
            style={{ background: "var(--btn-bg)", borderRadius: 8, height: 34, fontSize: 13, fontWeight: 600 }}>
            <i className="bi bi-list-ul"></i> Suppliers List
          </Link>
        </div>

        <form className="v2a-form" onSubmit={handleSubmit}>
            <div style={{ ...sectionCard, paddingBottom: 26 }}>
              {sectionTitle("bi-person", "Personal Details", "The primary contact for this supplier")}
              <div className="row g-3">
                {field("Title", false,
                  <select className="form-select" name="title" value={supplier.title} onChange={handleChange} style={inputStyle}>
                    {["Mr.", "Mrs.", "Ms.", "Dr."].map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                )}
                {field("First Name", true,
                  <input type="text" className="form-control" placeholder="First name" name="first_name"
                    value={supplier.first_name} onChange={handleChange} style={inputStyle} />
                )}
                {field("Last Name", true,
                  <input type="text" className="form-control" placeholder="Last name" name="last_name"
                    value={supplier.last_name} onChange={handleChange} style={inputStyle} />
                )}
                {field("Gender", true,
                  <select className="form-select" name="gender" value={supplier.gender} onChange={handleChange} style={inputStyle}>
                    <option value="">Select gender</option>
                    {["Male", "Female", "Other"].map(g => <option key={g} value={g.toLowerCase()}>{g}</option>)}
                  </select>
                )}
                {field("Mobile Number", true,
                  <><input type="text" inputMode="numeric" className="form-control" placeholder="10-digit mobile number" name="mobile"
                    value={supplier.mobile} onChange={handleMobileChange} style={inputStyle} />
                  {formAvailabilityMsg(availability.taken.mobile, availability.checking.mobile)}</>
                )}
                {field("Email ID", true,
                  <><input type="email" className="form-control" placeholder="name@company.com" name="email"
                    value={supplier.email} onChange={handleChange} style={inputStyle} />
                  {formAvailabilityMsg(availability.taken.email, availability.checking.email)}</>
                )}
                {field("WhatsApp Number", false,
                  <input type="text" inputMode="numeric" className="form-control" placeholder="10-digit WhatsApp number" name="whatsapp_number"
                    value={supplier.whatsapp_number} onChange={(e) => setSupplier({ ...supplier, whatsapp_number: e.target.value.replace(/\D/g, "").slice(0, 10) })} style={inputStyle} />
                )}
                {field("Supplier Code (Maximum 4 characters are allowed.)", true,
                  <><input type="text" className="form-control" placeholder="Supplier code" name="custom_code"
                  maxLength={4}
                    value={supplier.custom_code} onChange={handleChange} style={{ ...inputStyle, textTransform: "uppercase" }} />
                  {formAvailabilityMsg(availability.taken.custom_code, availability.checking.custom_code)}</>
                )}
              </div>
            </div>

            <div style={{ ...sectionCard, paddingBottom: 26 }}>
              {sectionTitle("bi-building", "Agency & Address", "Where the agency is registered")}
              <div className="row g-3">
                {field("Agency Name", true,
                  <input type="text" className="form-control" placeholder="Your agency's name" name="agency_name"
                    value={supplier.agency_name} onChange={handleChange} style={inputStyle} />
                )}
                {field("Website URL", false,
                  <input type="text" className="form-control" placeholder="https://www.example.com" name="website_url"
                    value={supplier.website_url} onChange={handleChange} style={inputStyle} />
                )}
                {field("Office Address", true,
                  <input type="text" className="form-control" placeholder="Street, area, landmark" name="address"
                    value={supplier.address} onChange={handleChange} style={inputStyle} />
                )}
                {field("Country", true,
                  <Select
                    options={countryOptions}
                    value={countryOptions.find(o => o.value === supplier.country_id) || null}
                    onChange={handleCountryChange}
                    placeholder="Select country"
                    isClearable
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />
                )}
                {field("State", true,
                  <Select
                    options={stateOptions}
                    value={stateOptions.find(o => o.value === supplier.state_id) || null}
                    onChange={handleStateChange}
                    placeholder="Select state"
                    isClearable
                    isDisabled={!supplier.country_id}
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />
                )}
                {field("City", true,
                  <Select
                    options={cityOptions}
                    value={cityOptions.find(o => o.value === supplier.city_id) || null}
                    onChange={(o) => setSupplier({ ...supplier, city_id: o ? o.value : "" })}
                    placeholder="Select city"
                    isClearable
                    isDisabled={!supplier.state_id}
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />
                )}
                {field("Pincode", true,
                  <input type="text" inputMode="numeric" className="form-control" placeholder="6-digit pincode" name="pincode"
                    value={supplier.pincode} onChange={handlePincodeChange} style={inputStyle} />
                )}
              </div>
            </div>

            <div style={{ ...sectionCard, paddingBottom: 26 }}>
              {sectionTitle("bi-bank", "Bank Details", "Account used for supplier payouts")}
              <div className="row g-3">
                {field("Account Holder Name", true,
                  <input type="text" className="form-control" placeholder="Name as per bank records" name="account_holder_name"
                    value={supplier.account_holder_name} onChange={handleChange} style={{ ...inputStyle, textTransform: "uppercase" }} />,
                  "col-12 col-md-6"
                )}
                {field("Account Number", true,
                  <input type="text" inputMode="numeric" className="form-control" placeholder="Bank account number" name="account_number"
                    value={supplier.account_number} onChange={handleChange} style={inputStyle} />,
                  "col-12 col-md-6"
                )}
                {field("Bank Name", true,
                  <Select
                    options={bankOptions}
                    value={bankOptions.find(o => o.value === supplier.bank_name) || null}
                    onChange={(o) => setSupplier({ ...supplier, bank_name: o ? o.value : "" })}
                    placeholder="Select bank"
                    isClearable
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />,
                  "col-12 col-md-6"
                )}
                {field("Branch Name", true,
                  <input type="text" className="form-control" placeholder="Branch name" name="branch_name"
                    value={supplier.branch_name} onChange={handleChange} style={{ ...inputStyle, textTransform: "uppercase" }} />,
                  "col-12 col-md-6"
                )}
                {field("Account Type", true,
                  <Select
                    options={ACCOUNT_TYPE_OPTIONS}
                    value={ACCOUNT_TYPE_OPTIONS.find(o => o.value === supplier.account_type) || null}
                    onChange={(o) => setSupplier({ ...supplier, account_type: o ? o.value : "" })}
                    placeholder="Select account type"
                    isClearable
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />,
                  "col-12 col-md-6"
                )}
                {field("IFSC Code", true,
                  <input type="text" className="form-control" placeholder="SBIN0001234" name="ifsc_code"
                    value={supplier.ifsc_code} onChange={handleChange} style={{ ...inputStyle, textTransform: "uppercase" }} />,
                  "col-12 col-md-6"
                )}
              </div>
            </div>

            <div style={{ ...sectionCard, paddingBottom: 26 }}>
              {sectionTitle("bi-shield-lock", "Login & Account Settings", "Credentials and account status")}
              <div className="row g-3">
                {field("User ID", false,
                  <input type="text" className="form-control" placeholder="Auto-filled from mobile number"
                    value={supplier.mobile} readOnly style={readOnlyStyle} />
                )}
                {field("Password", false,
                  <>
                    <PasswordInput className="form-control" placeholder="Minimum 6 characters" name="password" autoComplete="new-password"
                      value={supplier.password} onChange={handleChange} style={inputStyle} />
                    {isEdit && (
                      <div style={{ fontSize: 11, color: "rgb(233 13 72)", marginTop: 4 }}>
                        If you want to change password then fill it else keep blank
                      </div>
                    )}
                  </>
                )}
                {isEdit && field("System Supplier Code", false,
                  <input type="text" className="form-control" name="supplier_code"
                    value={supplier.supplier_code} readOnly
                    style={{ ...readOnlyStyle, textTransform: "uppercase" }} />
                )}
                {field("Status", false,
                  <Select
                    options={ACCOUNT_STATUS_OPTIONS}
                    value={ACCOUNT_STATUS_OPTIONS.find(o => o.value === supplier.status) || null}
                    onChange={(o) => setSupplier({ ...supplier, status: o ? o.value : "active" })}
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />
                )}
                {field("Inventory Status", false,
                  <Select
                    options={STATUS_OPTIONS}
                    value={STATUS_OPTIONS.find(o => o.value === supplier.inventory_status) || null}
                    onChange={(o) => setSupplier({ ...supplier, inventory_status: o ? o.value : "active" })}
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />
                )}
              </div>
            </div>

            <div className="d-flex justify-content-end gap-2">
              <Link to="/suppliers/list" className="btn px-4 d-flex align-items-center"
                style={{ background: "#fff", border: "1px solid #c3cad6", borderRadius: 8, height: 40, fontSize: 13, color: "#444" }}>
                Back
              </Link>
              <button type="submit" className="btn px-4 text-white fw-semibold"
                style={{ background: GRAD, border: "none", borderRadius: 8, height: 40, fontSize: 13 }}>
                {title}
              </button>
            </div>

        </form>
      </section>
    </>
  );
}

export default AddSupplier;
